import type { UseQueryOptions } from '@tanstack/react-query';

import { iamGetRolePrivilegeInfo } from '@crossnokaye/typescript-sdk/funcs/iamGetRolePrivilegeInfo';
import { iamGetUserAccessRules } from '@crossnokaye/typescript-sdk/funcs/iamGetUserAccessRules';
import {
  GetUserAccessRulesResponseBody,
  RolePrivilegeInfo,
} from '@crossnokaye/typescript-sdk/models/components';
import { SDKError } from '@crossnokaye/typescript-sdk/models/errors';
import {
  GetRolePrivilegeInfoResponse,
  GetUserAccessRulesRequest,
  GetUserAccessRulesResponse,
} from '@crossnokaye/typescript-sdk/models/operations';

import { AtlasClientFunction } from '@/client/types';
import { UserPrivilegeMap } from '@/hooks/access/types';
import { mapUserPrivileges } from '@/hooks/access/utils/map-user-privileges';

interface UserPrivilegeMapQueryProps {
  userId: string;
  client: AtlasClientFunction;
}

interface CombinedAPIResponse {
  userAccessRules: GetUserAccessRulesResponseBody | undefined;
  rolePrivilegeInfo: RolePrivilegeInfo | undefined;
}

export const userPrivilegeMapQuery = ({
  userId,
  client,
}: UserPrivilegeMapQueryProps): UseQueryOptions<
  CombinedAPIResponse,
  SDKError,
  UserPrivilegeMap
> => {
  return {
    queryKey: ['userPrivilegeMap', userId],
    queryFn: async (): Promise<CombinedAPIResponse> => {
      const [userAccessRulesResponse, rolePrivilegeInfoResponse] = await Promise.all([
        client<GetUserAccessRulesRequest, GetUserAccessRulesResponse>({
          atlasFunc: iamGetUserAccessRules,
          request: { userId },
        }),
        client<undefined, GetRolePrivilegeInfoResponse>({
          atlasFunc: iamGetRolePrivilegeInfo,
        }),
      ]);

      return {
        userAccessRules: userAccessRulesResponse.getUserAccessRulesResponseBody,
        rolePrivilegeInfo: rolePrivilegeInfoResponse.rolePrivilegeInfo,
      };
    },
    select: (data) => mapUserPrivileges(data.userAccessRules, data.rolePrivilegeInfo),
    enabled: !!userId,
    staleTime: Infinity,
  };
};
